import { useMutation, useQuery } from '@tanstack/react-query'
import { Badge, Box, Card, Flex, Heading, Text } from '@radix-ui/themes'
import { CheckIcon, UploadIcon } from '@radix-ui/react-icons'
import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import cn from 'classnames'
import { useTRPC, type TRPCOutputs } from '~/utils/trpc'
import { useAuth } from '~/auth/AuthProvider'
import { todayIso } from '~/utils/date'
import { IMAGE_DROP_ACCEPT, IMAGE_MAX_BYTES } from '../constants'
import { toUploadableImage } from '../heic'
import { uploadPreInstallationImage } from '../service'

type CreatedInstallation = TRPCOutputs['ops']['stationInstallations']['list'][number]

/**
 * A meter photo for one freshly created installation.
 *
 * Runs after the batch is committed, so it writes against a real id — which is why the wizard
 * creates before this step rather than carrying files through it. Each upload stands alone: a
 * failure here costs the photo, never the installation.
 */
const InstallationPhotoDrop = ({ installation }: { installation: CreatedInstallation }) => {
  const trpc = useTRPC()
  const { supabaseClient } = useAuth()
  const [state, setState] = useState<'idle' | 'uploading' | 'done'>('idle')
  const [error, setError] = useState<string | null>(null)

  const updateMutation = useMutation(trpc.ops.stationInstallations.update.mutationOptions())

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: IMAGE_DROP_ACCEPT,
    maxSize: IMAGE_MAX_BYTES,
    multiple: false,
    disabled: state === 'uploading',
    onDropRejected: (rejections) => setError(rejections[0]?.errors[0]?.message ?? 'File not accepted'),
    onDrop: async ([file]) => {
      if (!file) return
      setError(null)
      setState('uploading')
      try {
        const image = await toUploadableImage(file)
        const path = await uploadPreInstallationImage(supabaseClient, installation.installation_id, image)
        await updateMutation.mutateAsync({
          installationId: installation.installation_id,
          preInstallationImageUrl: path,
          preInstallationImageAddedAt: todayIso()
        })
        setState('done')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed')
        setState('idle')
      }
    }
  })

  return (
    <Flex direction="column" gap="1">
      <Flex align="center" gap="3">
        <Box className="grow min-w-0">
          <Text size="2" weight="medium" className="block truncate">
            {installation.name ?? `#${installation.installation_id}`}
          </Text>
          <Text size="1" color="gray">
            {installation.site?.name ?? '—'}
          </Text>
        </Box>
        {state === 'done' ? (
          <Badge color="green" variant="soft">
            <CheckIcon /> Uploaded
          </Badge>
        ) : (
          <Box
            {...getRootProps()}
            className={cn(
              'shrink-0 cursor-pointer rounded-[var(--radius-2)] border border-dashed px-3 py-2',
              isDragActive ? 'border-[color:var(--accent-9)] bg-[var(--accent-2)]' : 'border-[color:var(--gray-6)]'
            )}
          >
            <input {...getInputProps()} />
            <Flex align="center" gap="2">
              <UploadIcon />
              <Text size="1">{state === 'uploading' ? 'Uploading…' : 'Add photo'}</Text>
            </Flex>
          </Box>
        )}
      </Flex>
      {error ? (
        <Text size="1" color="red">
          {error}
        </Text>
      ) : null}
    </Flex>
  )
}

export const BatchPhotosStep = ({ installationIds }: { installationIds: number[] }) => {
  const trpc = useTRPC()
  const listQuery = useQuery({
    ...trpc.ops.stationInstallations.list.queryOptions({ installationIds }),
    enabled: installationIds.length > 0
  })

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Heading size="3">Meter photos</Heading>
        <Text size="2" color="gray">
          Optional. Add one now if you have it — the Shayp order needs it later, so it is one less thing to chase.
        </Text>
        {listQuery.isLoading ? (
          <Text size="2" color="gray">
            Loading…
          </Text>
        ) : (
          (listQuery.data ?? []).map((installation) => (
            <Box
              key={installation.installation_id}
              className="border-t border-t-[color:var(--gray-6)] pt-3 first:border-0 first:pt-0"
            >
              <InstallationPhotoDrop installation={installation} />
            </Box>
          ))
        )}
      </Flex>
    </Card>
  )
}

